import { Autocomplete, Button, Stack, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Field, Form, FormikProvider, useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  getPaymentMethods,
  getTrainerWithSearch,
  postTrainerMOUDetails,
  putTrainerMOUDetails,
} from '../../../../../redux/features/crdMigrationSlice';
import GlobalSelectField from '../../../../../components/mui/GlobalSelectField';
import { useSelector } from 'react-redux';
import GlobalFileUploadComponent from '../../../../../components/Fileupload/GlobalFileUploadComponent';
import { trainerMOUValidationSchema } from '../utils/trainerMOUValidationSchema';
import useHandleApiResponse from '../../../../../Hooks/useHandleApiResponse';

export default function AddnUpdateMOU({ successDispatch, successDispatchFn, row, setModalState }) {
  const dispatch = useDispatch();

  const paymentModes = useSelector((state) => state.crdMigration.paymentMethods);
  const [trainerSearch, setTrainerSearch] = useState('');
  const paymentModsLoading = useSelector((state) => state.crdMigration.loading);
  const submitLoading = useSelector((state) => state.crdMigration.submitLoading);
  const allTrainers = useSelector((state) => state.crdMigration.allTrainers.data);
  const trainerLoading = useSelector((state) => state.crdMigration.allTrainers.loading);
  const apiHelper = useHandleApiResponse();

  const formik = useFormik({
    onSubmit: (values) => {
      dispatch(
        row ? putTrainerMOUDetails({ data: values, id: row?.trainer_mou_id }) : postTrainerMOUDetails(values),
      ).then((res) =>
        apiHelper(res, {
          onSuccess: () => {
            dispatch(successDispatchFn(successDispatch));
            setModalState(false);
          },
        }),
      );
    },
    validationSchema: trainerMOUValidationSchema,
    enableReinitialize: true,
    initialValues: {
      trainer_id: row?.trainer_id ?? '',
      mou_amount: row?.mou_amount ?? '',
      mou_transaction_date: row?.mou_transaction_date ?? null,
      mou_payment_mode: row?.mou_payment_mode ?? '',
      mou_screenshot: row?.mou_screenshot ?? '',
      mou_screenshotDisplayName: '',
      mou_remarks: row?.mou_remarks ?? '',
    },
  });

  useEffect(() => {
    dispatch(getPaymentMethods());
  }, [dispatch]);

  useEffect(() => {
    !row && dispatch(getTrainerWithSearch(trainerSearch));
  }, [dispatch, trainerSearch, row]);

  return (
    <FormikProvider value={formik}>
      <Form>
        <Stack gap={2}>
          {row ? (
            <TextField label="Trainer Name" value={row?.full_name} disabled fullWidth />
          ) : (
            <Autocomplete
              options={allTrainers}
              isOptionEqualToValue={(opt, val) => opt.trainer_id === val?.trainer_id}
              getOptionKey={({ trainer_id }) => trainer_id ?? null}
              getOptionLabel={({ full_name }) => full_name ?? ''}
              fullWidth
              onChange={(_, v) => formik.setFieldValue('trainer_id', v?.trainer_id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Trainer Name"
                  helperText={Boolean(formik.touched.trainer_id) && formik.errors.trainer_id}
                  error={Boolean(formik.touched.trainer_id && formik.errors.trainer_id)}
                />
              )}
              onInputChange={(_, v) => setTrainerSearch(v)}
              loading={trainerLoading}
            />
          )}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              sx={{
                width: '100%',
              }}
              value={formik.values.mou_transaction_date ? dayjs(formik.values.mou_transaction_date) : null}
              label={'Transaction Date'}
              onChange={(v) => formik.setFieldValue('mou_transaction_date', v ? v.format('YYYY-MM-DD') : null)}
              slotProps={{
                textField: {
                  helperText: Boolean(formik.touched.mou_transaction_date) && formik.errors.mou_transaction_date,
                  error: Boolean(formik.touched.mou_transaction_date && formik.errors.mou_transaction_date),
                },
              }}
            />
          </LocalizationProvider>
          <Field
            as={TextField}
            name="mou_amount"
            label="Amount"
            fullWidth
            type="number"
            inputProps={{
              onWheel: (e) => e.target.blur(),
            }}
            helperText={Boolean(formik.touched.mou_amount) && formik.errors.mou_amount}
            error={Boolean(formik.touched.mou_amount && formik.errors.mou_amount)}
          />
          <GlobalFileUploadComponent
            formik={formik}
            fileBackendLink={'mou_screenshot'}
            fileDisplayName={'mou_screenshotDisplayName'}
          />
          <GlobalSelectField
            options={{
              formik,
              name: 'mou_payment_mode',
              label: 'PaymentMode',
              data: paymentModes,
              keys: {
                id: 'payment_method_id',
                value: 'payment_method_name',
              },
              sx: {
                width: '100%',
              },
              setFieldName: 'payment_method_name',
            }}
            disabled={paymentModsLoading}
          />
          <Field name="mou_remarks" as={TextField} multiline fullWidth rows={3} label="Remarks" />
          <Button
            variant="outlined"
            type="submit"
            sx={{
              alignSelf: 'flex-end',
            }}
            disabled={submitLoading}
          >
            {row ? (submitLoading ? 'updating...' : 'update') : submitLoading ? 'submitting...' : 'submit'}
          </Button>
        </Stack>
      </Form>
    </FormikProvider>
  );
}
